<template>
  <div class="py-5 md:px-5">
    <PageHeader :title="$route.params.type ? `New Report` : `Edit Report`" :subtitle="reportName" :back-route="{ name: 'ReportList' }" class="container">
      <div class="flex items-center space-x-3">
        <button class="btn btn-secondary" @click="showSaveModal = true" v-if="$route.params.type">
          Save...
        </button>
        <button class="btn btn-secondary" @click="showSaveModal = true" v-else>
          Edit
        </button>
        <button class="btn btn-primary" v-submitting="submitting"  @click="pullReport">
          Pull Report
        </button>
      </div>
    </PageHeader>

    <Modal title="Save Report" :open.sync="showSaveModal">
      <ReportForm class="p-6" :filter="filter" :report="savedReport" />
    </Modal>
    
    <div class="container">
      <!-- Loading -->
      <Loading v-if="loading" />

      <!-- Dynamic Report Component -->
      <component 
        ref="report"
        :is="reportType" 
        :report="pulledReport" />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import ReportForm from './ReportForm'
import AreaToAreaReport from './types/AreaToAreaReport'
import AreaVisitReport from './types/AreaVisitReport'
import AutomationDigest from './types/AutomationDigest'
import CostPerKilometreReport from './types/CostPerKilometreReport'
import DebriefReport from './types/DebriefReport'
import DepotArrivalReport from './types/DepotArrivalReport'
import DrivingDistanceReport from './types/DrivingDistanceReport'
import DrivingTimeReport from './types/DrivingTimeReport'
import DrivingTimelineReport from './types/DrivingTimelineReport'
import ExpenseReport from './types/ExpenseReport'
import InputExceptionReport from './types/InputExceptionReport'
import OrderLifecycleReport from './types/OrderLifecycleReport'
import RouteReport from './types/RouteReport'
import TripReport from './types/TripReport'
import UsageTimeReport from './types/UsageTimeReport'

export default {
  name: 'ReportView',

  metaInfo: {
    title: 'View Report'
  },

  components: {
    ReportForm,
    AreaToAreaReport,
    AreaVisitReport,
    AutomationDigest,
    CostPerKilometreReport,
    DebriefReport,
    DepotArrivalReport,
    DrivingDistanceReport,
    DrivingTimeReport,
    DrivingTimelineReport,
    ExpenseReport,
    InputExceptionReport,
    OrderLifecycleReport,
    RouteReport,
    TripReport,
    UsageTimeReport
  },

  async created () {
    if (this.$route.params.id) {
      await this.getReport()
    }
  },

  computed: {
    ...mapState(['reportTypes']),

    reportName () {
      if (this.$route.name === 'ReportView') {
        let name = ''
        this.reportTypes.sections.find((section) => {
          section.reports.find((report) => {
            if (report.type === this.$route.params.type) {
              name = report.name
            }
          })
        })
        return name
      } else if (this.$route.name === 'ReportEdit') {
        return this.savedReport ? this.savedReport.name : ''
      }
    },

    reportType () {
      if (this.$route.name === 'ReportView') {
        return this.$route.params.type
      } else if (this.$route.name === 'ReportEdit') {
        return this.savedReport ? this.savedReport.type : ''
      }
    },

    filter () {
      return this.$refs.report ? this.$refs.report.params.filter : {}
    }
  },

  data () {
    return {
      loading: false,
      submitting: false,
      pulledReport: null,
      showSaveModal: false,
      savedReport: null
    }
  },

  methods: {
    async pullReport () {
      try {
        this.submitting = true
        const { data } = await this.$api.get(`account/${this.$route.params.account}/report/${this.reportType}`, { 
          params: {
            page: this.$route.query.page || 1,
            ...this.$refs.report.params 
          }
        })
        this.pulledReport = data.report
      } catch (error) {
        console.error(error)
      } finally {
        this.submitting = false
      }
    },

    async downloadReport (filename) {
      try {
        this.submitting = true
        const { data } = await this.$api.post(`account/${this.$route.params.account}/report/${this.reportType}`, { 
          page: this.$route.query.page || 1,
          ...this.$refs.report.params 
        })
        const link = document.createElement('a')
        link.href = data.url
        link.download = `${filename || this.reportName}.csv`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (error) {
        console.error(error)
      } finally {
        this.submitting = false
      }
    },

    async getReport () {
      try {
        this.loading = true
        const url = `account/${this.$route.params.account}/reports/${this.$route.params.id}`
        const { data } = await this.$api.get(url)
        this.savedReport = data.report
        this.loading = false
      } catch (error) {
        console.error(error)
      }
    }
  },

  watch: {
    '$route.query': {
      handler () {
        this.pullReport()
      },
      deep: true
    }
  }
}
</script>